import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

// ================================
// TYPES
// ================================

// Supplier types
export interface PerformanceMetrics {
  totalDeliveries: number;
  onTimeDeliveries: number;
  onTimeDeliveryRate: number;
  lateDeliveries: number;
  averageDeliveryDelayDays: number;
  totalItemsReceived: number;
  itemsWithIssues: number;
  qualityRating: number;
  totalOrders: number;
  totalOrderValue: number;
  averageOrderValue: number;
  overallRating: number;
  lastUpdated: string;
}

export interface BankAccount {
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  branchName?: string;
}

export interface Supplier {
  _id: string;
  name: string;
  code?: string;
  contactPerson: string;
  phone: string;
  alternatePhone?: string;
  email: string;
  alternateEmail?: string;
  website?: string;
  address: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country: string;
  taxId?: string;
  registrationNumber?: string;
  bankAccount?: BankAccount;
  paymentTerms?: string;
  paymentTermsDays: number;
  creditLimit?: number;
  leadTimeDays?: number;
  minimumOrderValue?: number;
  deliveryDays?: string[];
  performanceMetrics?: PerformanceMetrics;
  suppliedIngredients?: Array<{ _id: string; name: string }>;
  suppliedCategories?: string[];
  isActive: boolean;
  isPreferred: boolean;
  rating?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierDocument {
  _id: string;
  supplierId: string;
  type: 'contract' | 'invoice' | 'certificate' | 'correspondence' | 'other';
  name: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  expiryDate?: string;
  notes?: string;
  uploadedBy: { _id: string; username: string };
  createdAt: string;
}

export interface SupplierPriceHistory {
  _id: string;
  supplierId: { _id: string; name: string; code?: string };
  ingredientId: { _id: string; name: string; unit: string };
  unitPrice: number;
  unit: string;
  effectiveFrom: string;
  effectiveTo?: string;
  previousPrice?: number;
  priceChangePercent?: number;
  priceChangeType?: 'increase' | 'decrease' | 'new' | 'no_change';
  alertAcknowledgedBy?: string;
  alertAcknowledgedAt?: string;
  recordedBy?: { _id: string; username: string };
  createdAt: string;
}

// Purchase Request types
export interface PRLineItem {
  _id: string;
  ingredientId?: { _id: string; name: string; unit: string };
  customItemName?: string;
  customItemDescription?: string;
  isFreeFormItem: boolean;
  quantity: number;
  unit: string;
  estimatedPrice?: number;
  suggestedQuantity?: number;
  forecastBasis?: string;
  originalQuantity?: number;
  modifiedBy?: { _id: string; username: string };
  modifiedAt?: string;
  modificationReason?: string;
  reason?: string;
}

export interface PurchaseRequest {
  _id: string;
  prNumber: string;
  branchId: { _id: string; name: string; code: string };
  status: 'draft' | 'pending' | 'pending_extra_approval' | 'approved' | 'rejected' | 'converted';
  items: PRLineItem[];
  totalEstimatedCost?: number;
  urgency: 'normal' | 'high' | 'urgent';
  urgencyReason?: string;
  requestedBy: { _id: string; username: string; email?: string };
  requestedDeliveryDate?: string;
  approvedBy?: { _id: string; username: string; email?: string };
  rejectedBy?: { _id: string; username: string };
  rejectionReason?: string;
  approvedAt?: string;
  hasFreeFormItems: boolean;
  extraApprovalRequired: boolean;
  extraApprovalBy?: { _id: string; username: string; email?: string };
  extraApprovalAt?: string;
  wasModifiedByHQ: boolean;
  hqModificationNotes?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Purchase Order types
export interface POSourceItem {
  prId: string;
  prLineItemId: string;
  quantity: number;
  branchId: string;
}

export interface POLineItem {
  _id: string;
  ingredientId?: { _id: string; name: string; unit: string };
  customItemName?: string;
  customItemDescription?: string;
  isFreeFormItem: boolean;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  receivedQuantity: number;
  sourceItems?: POSourceItem[];
}

export interface SplitDelivery {
  _id: string;
  expectedDate: string;
  quantity: number;
  unit: string;
  status: 'scheduled' | 'in_transit' | 'received' | 'cancelled';
  actualReceivedDate?: string;
  actualQuantity?: number;
  notes?: string;
}

export interface ConsolidatedFromInfo {
  prId: string;
  branchId: { _id: string; name: string; code: string };
  prNumber: string;
}

export interface SupplierComparisonEntry {
  supplierId: string;
  totalPrice: number;
  leadTimeDays: number;
  performanceRating: number;
  wasSelected: boolean;
}

export interface PurchaseOrder {
  _id: string;
  poNumber: string;
  supplierId: Supplier;
  prId?: string;
  prIds: Array<{ _id: string; prNumber: string; branchId: string }>;
  isConsolidated: boolean;
  consolidatedFrom?: ConsolidatedFromInfo[];
  deliveryBranchId?: { _id: string; name: string; code: string; address?: string };
  status: 'draft' | 'pending_approval' | 'approved' | 'sent' | 'confirmed' | 'partially_received' | 'completed' | 'cancelled';
  items: POLineItem[];
  subtotal: number;
  tax: number;
  taxRate: number;
  shipping: number;
  discount: number;
  total: number;
  expectedDeliveryDate?: string;
  splitDeliveries?: SplitDelivery[];
  hasSplitDelivery: boolean;
  supplierComparison?: SupplierComparisonEntry[];
  createdBy: { _id: string; username: string; email?: string };
  approvedBy?: { _id: string; username: string; email?: string };
  approvedAt?: string;
  sentAt?: string;
  notes?: string;
  internalNotes?: string;
  createdAt: string;
  updatedAt: string;
}

// GRN types
export interface GRNLineItem {
  _id: string;
  poLineItemId: string;
  ingredientId?: { _id: string; name: string; unit: string };
  customItemName?: string;
  isFreeFormItem: boolean;
  orderedQuantity: number;
  receivedQuantity: number;
  unit: string;
  supplierBatchNumber?: string;
  internalBatchNumber?: string;
  expiryDate?: string;
  condition: 'good' | 'damaged' | 'expired' | 'wrong_item' | 'quality_issue';
  qualityScore?: number;
  discrepancyType?: 'none' | 'shortage' | 'excess' | 'damaged' | 'wrong_item';
  discrepancyQuantity?: number;
  discrepancyAction?: 'pending_decision' | 'accepted' | 'rejected' | 'quarantine' | 'return_to_supplier';
  discrepancyDecisionBy?: { _id: string; username: string };
  discrepancyDecisionAt?: string;
  discrepancyDecisionNotes?: string;
  quarantineLocation?: string;
  quarantineReleaseDate?: string;
  notes?: string;
}

export interface DiscrepancySummary {
  shortageCount: number;
  excessCount: number;
  damagedCount: number;
  pendingDecisions: number;
}

export interface GoodsReceivedNote {
  _id: string;
  grnNumber: string;
  poId: PurchaseOrder | { _id: string; poNumber: string; supplierId: string };
  supplierId: Supplier | { _id: string; name: string; code?: string };
  warehouseId: { _id: string; name: string; code: string; address?: string };
  status: 'draft' | 'pending_review' | 'completed' | 'has_discrepancies';
  items: GRNLineItem[];
  receivedBy: { _id: string; username: string; email?: string };
  deliveryNote?: string;
  deliveryNoteNumber?: string;
  invoiceNumber?: string;
  invoiceAmount?: number;
  hasDiscrepancies: boolean;
  discrepancySummary?: DiscrepancySummary;
  variance?: number;
  reviewedBy?: { _id: string; username: string; email?: string };
  reviewedAt?: string;
  reviewNotes?: string;
  photos?: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Demand forecast types
export interface DemandForecast {
  ingredientId: string;
  branchId?: string;
  periodDays: number;
  totalConsumption: number;
  avgDailyConsumption: number;
  currentStock: number;
  suggestedQuantity: number;
  forecastBasis: string;
  daysOfSupply: number;
  safetyStockDays: number;
}

// Supplier comparison types
export interface SupplierComparison {
  supplierId: string;
  supplierName: string;
  supplierCode?: string;
  totalPrice: number;
  itemsAvailable: number;
  totalItems: number;
  coveragePercent: number;
  leadTimeDays: number;
  performanceRating: number;
  onTimeDeliveryRate: number;
  qualityRating: number;
  isPreferred: boolean;
}

// ================================
// SUPPLIER QUERIES
// ================================

export function useSuppliers(options?: {
  isActive?: boolean;
  isPreferred?: boolean;
  ingredientId?: string;
  search?: string;
  sortBy?: 'name' | 'rating' | 'leadTime';
}) {
  return useQuery({
    queryKey: ['suppliers', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.isActive !== undefined) params.append('isActive', String(options.isActive));
      if (options?.isPreferred !== undefined) params.append('isPreferred', String(options.isPreferred));
      if (options?.ingredientId) params.append('ingredientId', options.ingredientId);
      if (options?.search) params.append('search', options.search);
      if (options?.sortBy) params.append('sortBy', options.sortBy);

      const response = await apiClient.get<APIResponse<{ suppliers: Supplier[] }>>(
        `/purchasing/suppliers?${params.toString()}`
      );
      return response.data.data.suppliers;
    },
  });
}

export function useSupplier(id: string) {
  return useQuery({
    queryKey: ['supplier', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{
        supplier: Supplier;
        documents: SupplierDocument[];
        recentPrices: SupplierPriceHistory[];
      }>>(`/purchasing/suppliers/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useSupplierDocuments(supplierId: string, type?: string) {
  return useQuery({
    queryKey: ['supplier-documents', supplierId, type],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (type) params.append('type', type);

      const response = await apiClient.get<APIResponse<{ documents: SupplierDocument[] }>>(
        `/purchasing/suppliers/${supplierId}/documents?${params.toString()}`
      );
      return response.data.data.documents;
    },
    enabled: !!supplierId,
  });
}

export function useSupplierPriceHistory(supplierId: string, ingredientId?: string) {
  return useQuery({
    queryKey: ['supplier-price-history', supplierId, ingredientId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (ingredientId) params.append('ingredientId', ingredientId);

      const response = await apiClient.get<APIResponse<{ priceHistory: SupplierPriceHistory[] }>>(
        `/purchasing/suppliers/${supplierId}/price-history?${params.toString()}`
      );
      return response.data.data.priceHistory;
    },
    enabled: !!supplierId,
  });
}

export function usePriceAlerts() {
  return useQuery({
    queryKey: ['price-alerts'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ alerts: SupplierPriceHistory[] }>>(
        '/purchasing/price-alerts'
      );
      return response.data.data.alerts;
    },
  });
}

// ================================
// PURCHASE REQUEST QUERIES
// ================================

export function usePurchaseRequests(options?: {
  branchId?: string;
  status?: string;
  urgency?: string;
  hasFreeFormItems?: boolean;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['purchase-requests', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.branchId) params.append('branchId', options.branchId);
      if (options?.status) params.append('status', options.status);
      if (options?.urgency) params.append('urgency', options.urgency);
      if (options?.hasFreeFormItems !== undefined) params.append('hasFreeFormItems', String(options.hasFreeFormItems));
      if (options?.page) params.append('page', String(options.page));
      if (options?.limit) params.append('limit', String(options.limit));

      const response = await apiClient.get<APIResponse<{
        requests: PurchaseRequest[];
        pagination: { page: number; limit: number; total: number; pages: number };
      }>>(`/purchasing/purchase-requests?${params.toString()}`);
      return response.data.data;
    },
  });
}

export function usePurchaseRequest(id: string) {
  return useQuery({
    queryKey: ['purchase-request', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ request: PurchaseRequest }>>(
        `/purchasing/purchase-requests/${id}`
      );
      return response.data.data.request;
    },
    enabled: !!id,
  });
}

// ================================
// PURCHASE ORDER QUERIES
// ================================

export function usePurchaseOrders(options?: {
  supplierId?: string;
  status?: string;
  isConsolidated?: boolean;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['purchase-orders', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.supplierId) params.append('supplierId', options.supplierId);
      if (options?.status) params.append('status', options.status);
      if (options?.isConsolidated !== undefined) params.append('isConsolidated', String(options.isConsolidated));
      if (options?.page) params.append('page', String(options.page));
      if (options?.limit) params.append('limit', String(options.limit));

      const response = await apiClient.get<APIResponse<{
        orders: PurchaseOrder[];
        pagination: { page: number; limit: number; total: number; pages: number };
      }>>(`/purchasing/purchase-orders?${params.toString()}`);
      return response.data.data;
    },
  });
}

export function usePurchaseOrder(id: string) {
  return useQuery({
    queryKey: ['purchase-order', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ order: PurchaseOrder }>>(
        `/purchasing/purchase-orders/${id}`
      );
      return response.data.data.order;
    },
    enabled: !!id,
  });
}

// ================================
// GOODS RECEIVED NOTE QUERIES
// ================================

export function useGoodsReceivedNotes(options?: {
  purchaseOrderId?: string;
  warehouseId?: string;
  hasDiscrepancies?: boolean;
  status?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['goods-received-notes', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.purchaseOrderId) params.append('purchaseOrderId', options.purchaseOrderId);
      if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
      if (options?.hasDiscrepancies !== undefined) params.append('hasDiscrepancies', String(options.hasDiscrepancies));
      if (options?.status) params.append('status', options.status);
      if (options?.page) params.append('page', String(options.page));
      if (options?.limit) params.append('limit', String(options.limit));

      const response = await apiClient.get<APIResponse<{
        grns: GoodsReceivedNote[];
        pagination: { page: number; limit: number; total: number; pages: number };
      }>>(`/purchasing/goods-received?${params.toString()}`);
      return response.data.data;
    },
  });
}

export function useGoodsReceivedNote(id: string) {
  return useQuery({
    queryKey: ['goods-received-note', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ grn: GoodsReceivedNote }>>(
        `/purchasing/goods-received/${id}`
      );
      return response.data.data.grn;
    },
    enabled: !!id,
  });
}

// ================================
// DEMAND FORECAST QUERIES
// ================================

export function useDemandForecast(ingredientId: string, branchId?: string, days?: number) {
  return useQuery({
    queryKey: ['demand-forecast', ingredientId, branchId, days],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (days) params.append('days', String(days));

      const response = await apiClient.get<APIResponse<{ data: DemandForecast }>>(
        `/purchasing/demand-forecast/${ingredientId}?${params.toString()}`
      );
      return response.data.data;
    },
    enabled: !!ingredientId,
  });
}
